import { Breakpoint, type BreakpointSize } from '../../styles';
import {
  type ImageSources,
  MediaType,
  type MobileAndDesktopSources,
  type PictureSources,
  type SourcesByBreakpoint,
} from './types';

const BREAKPOINT_SIZE_TO_PX: Record<BreakpointSize, number> = {
  xs: Breakpoint.XS,
  sm: Breakpoint.SM,
  md: Breakpoint.MD,
  lg: Breakpoint.LG,
  xl: Breakpoint.XL,
};

export function createMediaQueryForRange(
  minPx: number,
  maxPx: number | undefined
): string {
  const lowerBound = `(min-width: ${minPx}px)`;
  const upperBound = `(max-width: ${maxPx}px)`;

  if (maxPx === undefined) {
    return lowerBound;
  }
  if (minPx === 0) {
    return upperBound;
  }
  return `${lowerBound} and ${upperBound}`;
}

/**
 * Get the pixel range for a breakpoint: from its min width up to (but not
 * including) the next breakpoint.
 *
 * @param currentSize - The breakpoint size for this range.
 * @param nextSize - The next breakpoint size in order, or undefined if this is
 *   the last range.
 */
export function getMediaQueryRange(
  currentSize: BreakpointSize,
  nextSize: BreakpointSize | undefined
): { minPx: number; maxPx: number | undefined } {
  const minPx = BREAKPOINT_SIZE_TO_PX[currentSize];
  const maxPx = !nextSize ? undefined : BREAKPOINT_SIZE_TO_PX[nextSize] - 1;

  return { minPx, maxPx };
}

export function getBreakpointSizesSortedByPx(
  sourcesByBreakpoint: SourcesByBreakpoint
): BreakpointSize[] {
  const sizes = Object.keys(sourcesByBreakpoint).filter(
    (key): key is BreakpointSize => key in BREAKPOINT_SIZE_TO_PX
  );
  return sizes.sort(
    (a, b) => BREAKPOINT_SIZE_TO_PX[a] - BREAKPOINT_SIZE_TO_PX[b]
  );
}

export const MOBILE_MEDIA_QUERY = `(max-width: ${Breakpoint.LG - 1}px)`;
export const DESKTOP_MEDIA_QUERY = `(min-width: ${Breakpoint.LG}px)`;

/**
 * Generate sources for a given media query range.
 *
 * @param sources - The image sources.
 * @param media - The media query range.
 * @returns Sources with embedded media query range.
 */
export function generateSourcesForBreakpoint(
  sources: ImageSources,
  media: string
): PictureSources[] {
  return [
    sources.webP && {
      src: sources.webP,
      type: MediaType.Webp as string,
      media,
    },
    sources.jp2 && {
      src: sources.jp2,
      type: MediaType.Jp2 as string,
      media,
    },
    sources.jpg && {
      src: sources.jpg,
      type: MediaType.Jpg as string,
      media,
    },
    sources.png && {
      src: sources.png,
      type: MediaType.Png as string,
      media,
    },
  ].filter((source): source is PictureSources => Boolean(source));
}

export function generateMobileSourcesForPicture(
  mobileSources: ImageSources
): PictureSources[] {
  return generateSourcesForBreakpoint(mobileSources, MOBILE_MEDIA_QUERY);
}

export function generateDesktopSourcesForPicture(
  desktopSources: ImageSources
): PictureSources[] {
  return generateSourcesForBreakpoint(desktopSources, DESKTOP_MEDIA_QUERY);
}

export function generateSourcesForPicture({
  mobileSources,
  desktopSources,
}: MobileAndDesktopSources) {
  // Check if mobile and desktop sources are identical
  const areSourcesIdentical =
    mobileSources.webP === desktopSources.webP &&
    mobileSources.jp2 === desktopSources.jp2 &&
    mobileSources.jpg === desktopSources.jpg &&
    mobileSources.png === desktopSources.png;

  // If sources are identical, generate a single set of sources without media queries
  if (areSourcesIdentical) {
    return [
      mobileSources.webP && {
        src: mobileSources.webP,
        type: MediaType.Webp as string,
      },
      mobileSources.jp2 && {
        src: mobileSources.jp2,
        type: MediaType.Jp2 as string,
      },
      mobileSources.jpg && {
        src: mobileSources.jpg,
        type: MediaType.Jpg as string,
      },
      mobileSources.png && {
        src: mobileSources.png,
        type: MediaType.Png as string,
      },
    ].filter((source): source is PictureSources => Boolean(source));
  }

  // Otherwise, generate separate sources for mobile and desktop
  const mobileSourcesForPreloader =
    generateMobileSourcesForPicture(mobileSources);

  const desktopSourcesForPreloader =
    generateDesktopSourcesForPicture(desktopSources);

  return [...mobileSourcesForPreloader, ...desktopSourcesForPreloader];
}

/**
 * Generate sources for a given breakpoint map using standard {@link Breakpoint}
 * sizes.
 *
 * @param sourcesByBreakpoint - The sources by breakpoint.
 * @returns Sources with embedded media query range.
 */
export function generateSourcesForPictureByBreakpoint(
  sourcesByBreakpoint: SourcesByBreakpoint
): PictureSources[] {
  const sizes = getBreakpointSizesSortedByPx(sourcesByBreakpoint);

  return sizes.flatMap((size, index) => {
    const sources = sourcesByBreakpoint[size];
    if (!sources) {
      return [];
    }

    const nextSize = sizes[index + 1];
    const { minPx, maxPx } = getMediaQueryRange(size, nextSize);
    const startPx = index === 0 ? 0 : minPx;
    const media = createMediaQueryForRange(startPx, maxPx);
    return generateSourcesForBreakpoint(sources, media);
  });
}
